import { useMemo } from "react";
import { Link } from "react-router-dom";
import type { InteractionWithId } from "../../hooks/useInteractions";
import type { EntityInfo } from "../../hooks/useEntityMap";
import type { EntityType } from "../../types";
import { todayKey } from "../../utils/calendarDay";
import {
  activityDayKey,
  activityEnd,
  activityStart,
  formatDuration,
  formatTime,
  type SegmentDayLookup,
  type SegmentZoneLookup,
} from "../../utils/tripDisplay";
import { TypeIcon } from "../common/TypeIcon";

/** How many of today's earlier activities to show before "Full day". */
const LIMIT = 4;

interface TodaySoFarProps {
  interactions: InteractionWithId[];
  /** Still open; they're already on screen above. */
  exclude: InteractionWithId[];
  entityDataMap: Record<string, EntityInfo>;
  typeOf: (i: InteractionWithId) => EntityType | undefined;
  onDay: SegmentDayLookup;
  onZone: SegmentZoneLookup;
  tripId: string;
  zone?: string;
}

/** Today's finished activities, newest first, as one quiet list. */
export function TodaySoFar({ interactions, exclude, entityDataMap, typeOf, onDay, onZone, tripId, zone }: TodaySoFarProps) {
  const rows = useMemo(() => {
    const skip = new Set(exclude.map((i) => i.id));
    const today = todayKey();
    return interactions
      .filter((i) => !skip.has(i.id) && activityDayKey(i, onDay, onZone) === today)
      .sort((a, b) => (activityStart(b, onDay)?.getTime() ?? 0) - (activityStart(a, onDay)?.getTime() ?? 0));
  }, [interactions, exclude, onDay, onZone]);

  if (rows.length === 0) return null;

  return (
    <section className="today-so-far" aria-labelledby="tsf-title">
      <h3 id="tsf-title" className="ctc-section-title">Earlier today</h3>
      {rows.slice(0, LIMIT).map((i) => {
        const info = i.entityId ? entityDataMap[i.entityId] : undefined;
        const name = info?.displayName || i.name || "Activity";
        const start = activityStart(i, onDay);
        const end = activityEnd(i, onDay);
        const secs = start && end ? (end.getTime() - start.getTime()) / 1000 : 0;
        const sub = [
          secs >= 60 ? formatDuration(secs) : null,
          i.postedWait ? `posted ${i.postedWait}m` : null,
        ].filter(Boolean).join(" · ");
        return (
          <Link key={i.id} to={`/trips/${tripId}/interactions/${i.id}`} className="tsf-row">
            <span className="tsf-time">{start ? formatTime(start, onZone(i.segmentId) ?? zone) : ""}</span>
            <span className="tsf-icon">
              {info?.imageUrl ? <img src={info.imageUrl} alt="" /> : <TypeIcon type={typeOf(i)} name={name} tile />}
            </span>
            <span className="tsf-text">
              <span className="tsf-name">{name}</span>
              {sub && <span className="tsf-sub">{sub}</span>}
            </span>
          </Link>
        );
      })}
      <Link to={`/trips/${tripId}`} className="tsf-all">
        {rows.length > LIMIT ? `Full day (${rows.length}) →` : "Full trip →"}
      </Link>
    </section>
  );
}
